/**
/**
 * PartnerFinancialSyncPanel.jsx
 * v3.21-1664 — Deal-derived Partner Financial Management.
 *
 * The panel is a READ-ONLY VIEW of Deal SSOT + authoritative ledgers
 * (partner_invoices, partner_payments).  The only actions retained
 * are REAL transaction entries:
 *
 *   Ready to Invoice  → [Create Invoice]  (writes partner_invoices)
 *   Invoiced          → [Record Wire]     (writes partner_payments)
 *   Wired             → [Awaiting payment] (badge — no action)
 *   Paid / Settled    → [Complete]        (badge — derived)
 *
 * "Mark Paid" and manual settlement controls have been removed — a
 * recorded wire (partner_payments row) is authoritative evidence.
 * SSOT / Settled state is derived automatically.  Every row's
 * [Refresh] is recalc-only (no mutation).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Loader2, RefreshCw, Landmark, AlertCircle, Clock, CheckCircle2,
  FileText, Send, DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString(); } catch { return String(v); }
};

const STATUS_STYLE = {
  ready_to_invoice: { label: 'Ready to Invoice', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  invoiced:         { label: 'Invoiced',         cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  wired:            { label: 'Wired',            cls: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  paid:             { label: 'Paid',             cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
};

const SUB_STYLE = {
  pending:  'bg-slate-100 text-slate-700 border-slate-300',
  invoiced: 'bg-blue-50 text-blue-700 border-blue-300',
  paid:     'bg-emerald-50 text-emerald-700 border-emerald-300',
  recorded: 'bg-indigo-50 text-indigo-700 border-indigo-300',
  unpaid:   'bg-slate-100 text-slate-700 border-slate-300',
  cancelled:'bg-rose-50 text-rose-700 border-rose-300',
};

const StatusPill = ({ row }) => {
  if (row.ssot_complete) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] px-1.5 py-0 font-medium bg-emerald-600 text-white border-emerald-700"
        data-testid={`pf-status-pill-ssot-${row.deal_id}`}
      >
        <CheckCircle2 className="h-3 w-3 mr-0.5 inline" /> Settled
      </Badge>
    );
  }
  const s = STATUS_STYLE[row.financial_status] || STATUS_STYLE.ready_to_invoice;
  return (
    <Badge
      variant="outline"
      className={`text-[10px] px-1.5 py-0 font-medium ${s.cls}`}
      data-testid={`pf-status-pill-${row.financial_status}`}
    >
      {s.label}
    </Badge>
  );
};

const SubPill = ({ status, kind, dealId }) => (
  <Badge
    variant="outline"
    className={`text-[9px] px-1 py-0 ${SUB_STYLE[status] || SUB_STYLE.pending}`}
    data-testid={`pf-sub-${kind}-${status}-${dealId}`}
  >
    {status}
  </Badge>
);

// ── Action button per row.  Contextual to the current DERIVED status. ──
// v3.21-1664: Manual "Mark Paid" removed.  Paid state is derived from the
// authoritative partner_payments ledger — recording the wire (real
// transaction entry) is the ONE money action; SSOT/Settled follows
// automatically on the next read.
const NextActionButton = ({ row, onCreateInvoice, onRecordWire, busy }) => {
  if (row.ssot_complete) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-300"
        data-testid={`pf-action-complete-${row.deal_id}`}
      >
        Complete
      </Badge>
    );
  }
  if (row.financial_status === 'ready_to_invoice') {
    return (
      <Button
        size="sm"
        className="h-6 text-[10px] px-2 bg-amber-600 hover:bg-amber-700 text-white"
        onClick={() => onCreateInvoice(row)}
        disabled={busy}
        data-testid={`pf-action-create-invoice-${row.deal_id}`}
      >
        <FileText className="h-3 w-3 mr-1" /> Create Invoice
      </Button>
    );
  }
  if (row.financial_status === 'invoiced') {
    return (
      <Button
        size="sm"
        className="h-6 text-[10px] px-2 bg-blue-600 hover:bg-blue-700 text-white"
        onClick={() => onRecordWire(row)}
        disabled={busy}
        data-testid={`pf-action-record-wire-${row.deal_id}`}
      >
        <Send className="h-3 w-3 mr-1" /> Record Wire
      </Button>
    );
  }
  // Wired but not yet SSOT complete — payment ledger will finalise it.
  return (
    <Badge
      variant="outline"
      className="text-[10px] px-1.5 py-0 bg-indigo-50 text-indigo-700 border-indigo-300"
      data-testid={`pf-action-awaiting-payment-${row.deal_id}`}
    >
      Awaiting payment
    </Badge>
  );
};

// ── Create Invoice dialog ──
const CreateInvoiceDialog = ({ row, open, onClose, onSubmit, saving }) => {
  const [amount, setAmount] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (open) {
      setAmount(String(row?.partner_amount_owed ?? ''));
      setInvoiceNumber('');
      setNotes('');
    }
  }, [open, row]);
  if (!row) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pf-create-invoice-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Partner Invoice</DialogTitle>
          <DialogDescription>
            {row.client_name} — {row.company_name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Amount (USD)</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="h-8 text-sm mt-1"
              data-testid="pf-invoice-amount-input"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Defaults to Partner Owed ({fmtMoney(row.partner_amount_owed)}).
            </p>
          </div>
          <div>
            <Label className="text-xs">Invoice Number (optional)</Label>
            <Input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Auto-generated if blank (PF-YYYYMMDD-XXXXXXXX)"
              className="h-8 text-sm mt-1"
              data-testid="pf-invoice-number-input"
            />
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="h-8 text-sm mt-1"
              data-testid="pf-invoice-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="pf-invoice-cancel">
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit({
              amount: amount === '' ? undefined : Number(amount),
              invoice_number: invoiceNumber || undefined,
              notes: notes || undefined,
            })}
            disabled={saving}
            data-testid="pf-invoice-submit"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FileText className="h-3 w-3 mr-1" />}
            Create Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Record Wire dialog ──
const RecordWireDialog = ({ row, open, onClose, onSubmit, saving }) => {
  const [wireRef, setWireRef]   = useState('');
  const [amount, setAmount]     = useState('');
  const [wireDate, setWireDate] = useState('');
  const [bankName, setBankName] = useState('');
  const [notes, setNotes]       = useState('');
  useEffect(() => {
    if (open) {
      setWireRef('');
      setAmount(String(row?.final_wire_amount ?? ''));
      setWireDate(new Date().toISOString().slice(0, 10));
      setBankName('');
      setNotes('');
    }
  }, [open, row]);
  if (!row) return null;
  const canSubmit = wireRef.trim().length > 0;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="pf-record-wire-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record Wire Transfer</DialogTitle>
          <DialogDescription>
            {row.client_name} — Invoice {row.partner_invoice_number || '(pending)'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Wire Reference # <span className="text-red-500">*</span></Label>
            <Input
              value={wireRef}
              onChange={(e) => setWireRef(e.target.value)}
              placeholder="Bank-issued wire confirmation number"
              className="h-8 text-sm mt-1"
              data-testid="pf-wire-reference-input"
              required
            />
          </div>
          <div>
            <Label className="text-xs">Amount (USD)</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="h-8 text-sm mt-1"
              data-testid="pf-wire-amount-input"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Defaults to Final Wire ({fmtMoney(row.final_wire_amount)}).
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Wire Date</Label>
              <Input
                type="date"
                value={wireDate}
                onChange={(e) => setWireDate(e.target.value)}
                className="h-8 text-sm mt-1"
                data-testid="pf-wire-date-input"
              />
            </div>
            <div>
              <Label className="text-xs">Bank Name</Label>
              <Input
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                className="h-8 text-sm mt-1"
                data-testid="pf-wire-bank-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="h-8 text-sm mt-1"
              data-testid="pf-wire-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="pf-wire-cancel">
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit({
              wire_reference: wireRef.trim(),
              amount: amount === '' ? undefined : Number(amount),
              wire_date: wireDate || undefined,
              bank_name: bankName || undefined,
              notes: notes || undefined,
            })}
            disabled={!canSubmit || saving}
            data-testid="pf-wire-submit"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
            Record Wire
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Main panel ──
const PartnerFinancialSyncPanel = ({ companyId }) => {
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [error, setError]             = useState(null);
  const [data, setData]               = useState(null);
  const [busyRowId, setBusyRowId]     = useState(null);
  const [invDialog, setInvDialog]     = useState({ open: false, row: null, saving: false });
  const [wireDialog, setWireDialog]   = useState({ open: false, row: null, saving: false });

  const load = async () => {
    if (!companyId) return;
    setError(null);
    try {
      const { data: resp } = await api.get(`/companies/${companyId}/partner-financial-sync`);
      setData(resp);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load partner financial sync');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
  }, [companyId]); // load intentionally stable via companyId scope

  const rows = useMemo(() => data?.syncs || [], [data]);
  const totals = data?.totals;

  const refreshRow = async (row) => {
    setBusyRowId(row.deal_id);
    try {
      await api.post(`/deals/${row.deal_id}/partner-financial-sync/refresh`);
      toast.success('Sync refreshed from live records');
      await load();
    } catch (e) {
      toast.error(`Refresh failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusyRowId(null);
    }
  };

  const submitCreateInvoice = async (payload) => {
    setInvDialog((s) => ({ ...s, saving: true }));
    try {
      const { data: resp } = await api.post(
        `/deals/${invDialog.row.deal_id}/partner-financial-sync/create-invoice`,
        payload,
      );
      toast.success(
        resp.created
          ? `Invoice ${resp.invoice.invoice_number} created`
          : `Invoice ${resp.invoice.invoice_number} already existed — linked.`,
      );
      setInvDialog({ open: false, row: null, saving: false });
      await load();
    } catch (e) {
      toast.error(`Create invoice failed: ${e?.response?.data?.detail || e.message}`);
      setInvDialog((s) => ({ ...s, saving: false }));
    }
  };

  const submitRecordWire = async (payload) => {
    setWireDialog((s) => ({ ...s, saving: true }));
    try {
      await api.post(
        `/deals/${wireDialog.row.deal_id}/partner-financial-sync/record-wire`,
        payload,
      );
      toast.success('Wire recorded');
      setWireDialog({ open: false, row: null, saving: false });
      await load();
    } catch (e) {
      toast.error(`Record wire failed: ${e?.response?.data?.detail || e.message}`);
      setWireDialog((s) => ({ ...s, saving: false }));
    }
  };

  // v3.21-1664: markPaid removed. Paid state is derived from
  // partner_payments — no secondary settlement action.

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="partner-financial-sync-loading">
        <CardContent className="py-6 flex items-center gap-3 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading partner financial sync…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-rose-200 bg-rose-50/20" data-testid="partner-financial-sync-error">
        <CardContent className="py-4 text-sm text-rose-700 flex items-center gap-2">
          <AlertCircle className="h-4 w-4" /> {String(error)}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-indigo-200 bg-white" data-testid="partner-financial-sync-panel">
        <CardHeader className="py-3 px-4 border-b border-indigo-100 bg-indigo-50/30 flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-sm font-semibold flex items-center gap-2 text-indigo-900">
              <Landmark className="h-4 w-4 text-indigo-700" />
              Partner Financial Management
              <Badge
                variant="outline"
                className="ml-2 text-[9px] px-1 py-0 border-indigo-300 text-indigo-700 uppercase tracking-wide"
                data-testid="pf-sync-version"
              >
                PARTNER-FIN-001
              </Badge>
            </CardTitle>
            <p className="text-[11px] text-indigo-700/80 mt-0.5">
              Every deal that reached Paid In Full is actioned here from Ready to Invoice
              through SSOT. Rows tagged <strong>Settled</strong> are fully closed and excluded
              from Outstanding Owed.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setRefreshing(true); load(); }}
            disabled={refreshing}
            className="text-[11px] inline-flex items-center gap-1 border border-indigo-300 text-indigo-700 hover:bg-indigo-50 rounded px-2 py-1 disabled:opacity-50"
            data-testid="pf-sync-refresh-btn"
          >
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Refresh All
          </button>
        </CardHeader>

        <CardContent className="px-4 py-3 space-y-3">
          {/* KPI roll-ups */}
          {totals && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs" data-testid="pf-sync-totals">
              <div className="rounded-lg border border-red-200 bg-red-50/60 px-3 py-2">
                <div className="text-[10px] text-red-600 uppercase tracking-wide">Outstanding Owed</div>
                <div className="font-bold text-red-700 text-sm" data-testid="pf-outstanding-owed">
                  {fmtMoney(totals.outstanding_partner_amount_owed)}
                </div>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2">
                <div className="text-[10px] text-amber-700 uppercase tracking-wide">Ready to Invoice</div>
                <div className="font-bold text-amber-800 text-sm" data-testid="pf-ready-to-invoice-count">
                  {totals.ready_to_invoice_count}
                </div>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2">
                <div className="text-[10px] text-blue-700 uppercase tracking-wide">Invoiced</div>
                <div className="font-bold text-blue-800 text-sm" data-testid="pf-invoiced-count">
                  {totals.invoiced_count}
                </div>
              </div>
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2">
                <div className="text-[10px] text-indigo-700 uppercase tracking-wide">Wired</div>
                <div className="font-bold text-indigo-800 text-sm" data-testid="pf-wired-count">
                  {totals.wired_count}
                </div>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2">
                <div className="text-[10px] text-emerald-700 uppercase tracking-wide">Paid</div>
                <div className="font-bold text-emerald-800 text-sm" data-testid="pf-paid-count">
                  {totals.paid_count}
                </div>
              </div>
              <div className="rounded-lg border border-emerald-300 bg-emerald-100/70 px-3 py-2">
                <div className="text-[10px] text-emerald-800 uppercase tracking-wide">Settled (SSOT)</div>
                <div className="font-bold text-emerald-900 text-sm" data-testid="pf-ssot-complete-count">
                  {totals.ssot_complete_count || 0}
                </div>
              </div>
            </div>
          )}

          {rows.length === 0 ? (
            <div
              className="text-xs text-slate-500 italic px-2 py-4 border border-dashed border-slate-200 rounded text-center"
              data-testid="pf-sync-empty"
            >
              No deals have reached Paid In Full + All Parties Settled yet.
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-slate-200" data-testid="pf-sync-table">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-2 py-1.5">Deal</th>
                    <th className="text-left px-2 py-1.5">Client</th>
                    <th className="text-left px-2 py-1.5">Paid In Full</th>
                    <th className="text-right px-2 py-1.5">Gross</th>
                    <th className="text-right px-2 py-1.5">Partner Owed</th>
                    <th className="text-right px-2 py-1.5">Final Wire</th>
                    <th className="text-left px-2 py-1.5">Invoice</th>
                    <th className="text-left px-2 py-1.5">Wire</th>
                    <th className="text-left px-2 py-1.5">Payment</th>
                    <th className="text-left px-2 py-1.5">Status</th>
                    <th className="text-left px-2 py-1.5">Next Action</th>
                    <th className="text-left px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const rowBusy = busyRowId === r.deal_id;
                    return (
                      <tr
                        key={r.id}
                        className={`border-t border-slate-100 hover:bg-slate-50/60 ${
                          r.ssot_complete ? 'bg-emerald-50/30' : ''
                        }`}
                        data-testid={`pf-sync-row-${r.deal_id}`}
                        data-ssot-complete={r.ssot_complete ? '1' : '0'}
                      >
                        <td className="px-2 py-1.5 font-mono text-[10px] text-slate-600">
                          {r.deal_number || r.deal_id?.slice(0, 8)}
                        </td>
                        <td className="px-2 py-1.5 text-slate-800 font-medium" data-testid={`pf-sync-client-${r.deal_id}`}>
                          {r.client_name || '—'}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">{fmtDate(r.paid_in_full_at)}</td>
                        <td className="px-2 py-1.5 text-right text-slate-800">{fmtMoney(r.gross_deal_amount)}</td>
                        <td
                          className={`px-2 py-1.5 text-right font-semibold ${
                            r.ssot_complete ? 'text-emerald-700 line-through decoration-emerald-500/60' : 'text-red-700'
                          }`}
                          data-testid={`pf-partner-owed-${r.deal_id}`}
                        >
                          {fmtMoney(r.partner_amount_owed)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-emerald-700">{fmtMoney(r.final_wire_amount)}</td>
                        <td className="px-2 py-1.5"><SubPill status={r.invoice_status} kind="invoice" dealId={r.deal_id} /></td>
                        <td className="px-2 py-1.5"><SubPill status={r.wire_status}    kind="wire"    dealId={r.deal_id} /></td>
                        <td className="px-2 py-1.5"><SubPill status={r.payment_status} kind="payment" dealId={r.deal_id} /></td>
                        <td className="px-2 py-1.5"><StatusPill row={r} /></td>
                        <td className="px-2 py-1.5">
                          <NextActionButton
                            row={r}
                            busy={rowBusy}
                            onCreateInvoice={(row) => setInvDialog({ open: true, row, saving: false })}
                            onRecordWire={(row)   => setWireDialog({ open: true, row, saving: false })}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <button
                            type="button"
                            title="Refresh this row from live client + invoice + wire records"
                            onClick={() => refreshRow(r)}
                            disabled={rowBusy}
                            className="text-[10px] inline-flex items-center gap-1 border border-slate-200 text-slate-600 hover:bg-slate-100 rounded px-1.5 py-0.5 disabled:opacity-50"
                            data-testid={`pf-refresh-row-${r.deal_id}`}
                          >
                            {rowBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p
            className="text-[10px] text-slate-400 italic pt-1 border-t border-slate-100 flex items-center gap-1"
            data-testid="pf-arch-note"
          >
            <Clock className="h-2.5 w-2.5" />
            SSOT = invoice raised · wire completed · payment marked paid. Settled rows are excluded from Outstanding Owed.
          </p>
        </CardContent>
      </Card>

      <CreateInvoiceDialog
        open={invDialog.open}
        row={invDialog.row}
        saving={invDialog.saving}
        onClose={() => setInvDialog({ open: false, row: null, saving: false })}
        onSubmit={submitCreateInvoice}
      />
      <RecordWireDialog
        open={wireDialog.open}
        row={wireDialog.row}
        saving={wireDialog.saving}
        onClose={() => setWireDialog({ open: false, row: null, saving: false })}
        onSubmit={submitRecordWire}
      />
    </>
  );
};

export default PartnerFinancialSyncPanel;
export { PartnerFinancialSyncPanel };
